/**
 * Column definitions for the Ads Manager tabs. Structure mirrors
 * `dashboard/lib/columns.ts`. Each tab has fixed columns (always
 * visible, not reorderable) and movable columns that persist order
 * + visibility to localStorage.
 */

export type AdsManagerColumnType = "string" | "number"

export interface AdsManagerColumn {
  id: string
  label: string
  type: AdsManagerColumnType
  tooltip?: string
}

// ---------------------------------------------------------------------------
// Roots columns
// ---------------------------------------------------------------------------

export const ROOTS_COLUMNS: readonly AdsManagerColumn[] = [
  { id: "status", label: "Status", type: "string" },
  { id: "budget", label: "Budget", type: "number" },
  { id: "bid", label: "Bid", type: "number" },
  { id: "cost", label: "Cost", type: "number" },
  { id: "revenue", label: "Revenue", type: "number" },
  { id: "profit", label: "Profit", type: "number" },
  { id: "roi", label: "ROI", type: "number" },
  { id: "gclicks", label: "gClick", type: "number", tooltip: "Google clicks" },
  { id: "cpa", label: "CPA", type: "number", tooltip: "Cost per conversion (cost / gclicks)" },
  { id: "rpc", label: "RPC", type: "number", tooltip: "Revenue per click (revenue / gclicks)" },
  { id: "conversions", label: "Conv", type: "number", tooltip: "Conversions" },
  { id: "clicks", label: "Click", type: "number" },
  { id: "cpc", label: "CPC", type: "number" },
  { id: "cpm", label: "CPM", type: "number" },
  { id: "ctr", label: "CTR", type: "number" },
  { id: "cvr", label: "CVR", type: "number" },
  {
    id: "p1_cvr",
    label: "p1CVR",
    type: "number",
    tooltip:
      "Roots g.pCTR — article → tile. Green ≥50%, amber 30–50%, red <30%",
  },
  {
    id: "p2_cvr",
    label: "p2CVR",
    type: "number",
    tooltip:
      "Roots g.CTR — SERP → ad. Green ≥12%, amber 5–12%, red <5% (Zero Coverage)",
  },
  { id: "impressions", label: "Impr", type: "number", tooltip: "Impressions" },
] as const

// ---------------------------------------------------------------------------
// ADW columns
// ---------------------------------------------------------------------------

export const ADW_COLUMNS: readonly AdsManagerColumn[] = [
  { id: "status", label: "Status", type: "string" },
  { id: "visits", label: "Visits", type: "number" },
  { id: "two_flow", label: "2Flow", type: "number" },
  { id: "gclicks", label: "gClick", type: "number", tooltip: "Google clicks" },
  { id: "revenue", label: "Revenue", type: "number" },
  { id: "rpm", label: "RPM", type: "number" },
  { id: "rpc", label: "RPC", type: "number", tooltip: "Revenue per click (revenue / gclicks)" },
  { id: "one_to_two_flow", label: "1->2 Flow", type: "number" },
  { id: "cost", label: "Cost", type: "number" },
  { id: "profit", label: "Profit", type: "number" },
  { id: "roi", label: "ROI", type: "number" },
  { id: "cpa", label: "CPA", type: "number", tooltip: "Cost per conversion (cost / gclicks)" },
  { id: "conversions", label: "Conv", type: "number", tooltip: "Conversions" },
  { id: "clicks", label: "Click (TT)", type: "number" },
  { id: "cpc", label: "CPC", type: "number" },
  { id: "cpm", label: "CPM", type: "number" },
  { id: "ctr", label: "CTR", type: "number" },
  { id: "cvr", label: "CVR", type: "number" },
  { id: "impressions", label: "Impr", type: "number", tooltip: "Impressions" },
] as const

// ---------------------------------------------------------------------------
// KLOW columns (same layout as Roots)
// ---------------------------------------------------------------------------

export const KLOW_COLUMNS: readonly AdsManagerColumn[] = ROOTS_COLUMNS

// ---------------------------------------------------------------------------
// Unassigned columns (same as Roots)
// ---------------------------------------------------------------------------

export const UNASSIGNED_COLUMNS: readonly AdsManagerColumn[] = ROOTS_COLUMNS

// ---------------------------------------------------------------------------
// localStorage persistence
// ---------------------------------------------------------------------------

export const ROOTS_COLUMN_STORAGE_KEY = "adsManagerRootsColumnOrder"
export const ROOTS_VISIBILITY_STORAGE_KEY = "adsManagerRootsColumnVisibility"
export const ADW_COLUMN_STORAGE_KEY = "adsManagerAdwColumnOrder"
export const ADW_VISIBILITY_STORAGE_KEY = "adsManagerAdwColumnVisibility"
export const KLOW_COLUMN_STORAGE_KEY = "adsManagerKlowColumnOrder"
export const KLOW_VISIBILITY_STORAGE_KEY = "adsManagerKlowColumnVisibility"
export const UNASSIGNED_COLUMN_STORAGE_KEY = "adsManagerUnassignedColumnOrder"
export const UNASSIGNED_VISIBILITY_STORAGE_KEY = "adsManagerUnassignedColumnVisibility"

/**
 * Load saved column order, validating against current defaults.
 * Discards saved order if columns have changed.
 */
export function loadColumnOrder(
  storageKey: string,
  defaults: readonly AdsManagerColumn[],
): string[] {
  try {
    const saved = globalThis.localStorage?.getItem(storageKey)
    if (saved) {
      const parsed: unknown = JSON.parse(saved)
      if (Array.isArray(parsed)) {
        const ids = parsed.map((entry) =>
          typeof entry === "string"
            ? entry
            : entry && typeof entry === "object" && "id" in entry
              ? String((entry as { id: unknown }).id)
              : "",
        )
        const savedSet = new Set(ids.filter(Boolean))
        const defaultSet = new Set(defaults.map((c) => c.id))
        if (
          savedSet.size === defaultSet.size &&
          [...savedSet].every((id) => defaultSet.has(id))
        ) {
          return ids.filter(Boolean)
        }
      }
    }
  } catch {
    // fall through
  }
  return defaults.map((c) => c.id)
}

export function saveColumnOrder(storageKey: string, order: string[]): void {
  try {
    globalThis.localStorage?.setItem(storageKey, JSON.stringify(order))
  } catch {
    // silent
  }
}

/**
 * Load column visibility map. Returns a Set of hidden column IDs.
 */
export function loadColumnVisibility(storageKey: string): Set<string> {
  try {
    const saved = globalThis.localStorage?.getItem(storageKey)
    if (saved) {
      const parsed: unknown = JSON.parse(saved)
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((x): x is string => typeof x === "string"))
      }
    }
  } catch {
    // fall through
  }
  return new Set()
}

export function saveColumnVisibility(
  storageKey: string,
  hiddenIds: Set<string>,
): void {
  try {
    globalThis.localStorage?.setItem(
      storageKey,
      JSON.stringify([...hiddenIds]),
    )
  } catch {
    // silent
  }
}

/**
 * Map tab name to column definitions.
 */
export function columnsForTab(
  tab: "roots" | "adw" | "klow" | "brm" | "unassigned",
): readonly AdsManagerColumn[] {
  switch (tab) {
    case "roots":
      return ROOTS_COLUMNS
    case "klow":
      return KLOW_COLUMNS
    case "adw":
      return ADW_COLUMNS
    case "unassigned":
      return UNASSIGNED_COLUMNS
    case "brm":
      return ROOTS_COLUMNS // placeholder
  }
}
